import {OSkinableInit} from "/@back@/commons/basis.js";
import {Action, ActionHackCtx, ActionMenu, EButtonUiContext, IAction} from "/@lib@/commons/actions.js";
import {SimpleTagArea} from "/@lib@/commons/areas.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {Desk} from "/@lib@/commons/desk.js";
import {ISecurityCtxPointer} from "/@lib@/commons/security.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JSX} from "/@lib@/commons/xml/dom.js";

import {AppFrameDeskFeat, IApp, IAppBuilder, IAppCtx, JAppDef} from "/@back@/core/appFrame.js";
import {CheckPrefArea, SelectPrefArea, UserPrefPageConfig} from "/@back@/core/dialogs/userPrefEditorConfig.js";

import "/@back@/wsp/plugins/plugins_Perms.js";
import "/@back@/wsp/actions/actions_Perms.js";

import {srcRef} from "/@lib@/wsp/src.js";
import {IWspEnv, IWspUiEnv, Wsp, wspCd} from "/@lib@/wsp/wsp.js";

import type {WspList} from "/@back@/wsp/views/wspList.js";
import type {IChainEnv} from "/@lib@/wsp/chain.js";
import type {OWspAppInit, WspApp} from "/@back@/wsp/apps/wspApp.js";
import type {OWspDocAppInit, WspDocApp} from "/@back@/wsp/apps/wspDocApp.js";
import type {ICreateWspCtx, IWspActionCtx} from "/@back@/wsp/actions/wspActions.js";
import {CreateWspAction, ImportWspAction, ManageWspsAction} from "/@back@/wsp/actions/chainActions.js";


/**
 * Accès aux ateliers d'un chain dans une appFrame.
 * A appeler au cours du processus d'init du desk (qui doit disposer de AppFrameDeskFeat).
 *
 * @param reg Registre à exploiter correspondant au chain, nécessaire en cas de front multi-universe.
 */
export function initApp(reg: IReg<IChainEnv> = REG.reg) {

	class WrapReg extends ActionHackCtx<Element, IReg<IChainEnv>> {
		constructor(sub: IAction<IReg<IChainEnv>>, public reg: IReg<IChainEnv>) {
			super(sub);
		}

		wrapCtx(ctx: Element): IReg<IChainEnv> {return this.reg}

		initButtonNode(buttonNode: Element, ctx: Element) {
			//Affectation du bon registre dans le contexte graphique.
			(buttonNode as IRegPointer<IChainEnv>).reg = this.reg;
			this.sub.initButtonNode(buttonNode, this.reg);
		}
	}

	function wrap(action: IAction<IReg<IChainEnv>>): IAction<Element> {
		return new WrapReg(action, reg);
	}

	const plgCode = reg.getPref<string>("plg.code", "xChain");
	const plgName = reg.getPref<string>("plg.name", "Ateliers");
	const plgCheckUniverse = reg.getPref<boolean>("plg.checkUniverse", true);
	//const plgUniverseCode = reg.getPref<string>("plg.universe", "xChain");

	const d = desk as AppFrameDeskFeat;
	d.addAppBuilder(new WspAppBuilder(reg, plgCheckUniverse));
	d.addAppBuilder(new WspDocAppBuilder(reg, plgCheckUniverse));

	d.surveyWsConnexion(reg.env.universe.wsFrames.ws);

	// Création du menu 'Ateliers'
	const wspsActionListCode = "app:wsps:" + plgCode + ":actions";
	const wspsAction = wrap(new ActionMenu<IReg<IChainEnv>>('wsps')
		.setLabel(plgName)
		//.setIcon("/@skin@/wsp/objects/wspList.svg")
		.setSkinOver("appframeHeader_wsps")
		.setActionLists("app:wsps:actions " + wspsActionListCode)
		.setVisible((ctx) => {
			if (!REG.getReg(ctx).hasPerm(["ui.wspApp", "ui.wspDocApp"], (ctx as ISecurityCtxPointer).securityCtx)) return false;
		})
		.requireVisiblePerm(["ui.wspsPlg"])
	);

	REG.reg.addToList("appframe:header:toolbar", "wsps_" + plgCode, 1, wspsAction);

	//Menu Ateliers
	reg.addToList(wspsActionListCode, 'wspList', 1, wrap(new WspListAction('wspList')), 100);
	reg.addToList(wspsActionListCode, 'addWsp', 1, wrap(new CreateWspAction().requireVisiblePerm("ui.wspsPlg.create")), 110);
	reg.addToList(wspsActionListCode, 'importWsp', 1, wrap(new ImportWspAction().requireVisiblePerm("ui.wspsPlg.import")), 120);
	reg.addToList(wspsActionListCode, 'manageWsps', 1, wrap(new ManageWspsAction().requireVisiblePerm("ui.wspsPlg.manage")), 200);

	//Prefs
	const userPrefsListCd = `user:prefs:${plgCode}`;
	REG.reg.addToList(UserPrefPageConfig.LIST_userPrefPages, `prl_${plgCode}`, 1, new UserPrefPageConfig(reg).setAreasList(userPrefsListCd).setLabel(plgName), 2);
	reg.addToList(userPrefsListCd, "wspAppPrefered", 1, new CheckPrefArea("wspAppPrefered").setLabel("Privilégier le mode explorateur au mode plan").setDescription("L'accès par défaut à l'atelier est toujours le mode explorateur, même si le mode plan existe").setCheckStateIfNone(true));
	reg.addToList(userPrefsListCd, "wspApp:tools:generators", 1, new SelectPrefArea("wspApp:tools:generators").setLabel("Emplacement du volet 'Publications' (mode explorateur)'").setBuildOptions((selectElt, currentVal) => {
		if (!currentVal) currentVal = "";

		function add(cd: string, label: string) {
			selectElt.append(<option value={cd} selected={currentVal === cd ? "" : undefined}>{label}</option>);
		}

		add("lt", "À gauche au-dessus");
		add("lb", "À gauche au-dessous");
		add("bl", "En bas à gauche");
		add("br", "En bas à droite");
		add("rt", "À droite au-dessus");
		add("rb", "À droite au-dessous");
		add("", "Non spécifié");
	}));
	reg.addToList(userPrefsListCd, "showFlapTabs", 1, new CheckPrefArea("showFlapTabs").setLabel("Masquer les onglets des volets fermés (mode explorateur)").setDescription("Le menu principal de l'atelier permet d'accéder aux volets fermés").setCheckStateIfNone(true).setInvertCheck().setReloadAppNeeded());
}


/** Liste les ateliers dans le menu. */
class WspListAction extends Action<IReg<IChainEnv>> {

	buildCustomButton(ctx: IReg<IChainEnv>, uiContext: EButtonUiContext, parent?: Element): Element {
		import("/@back@/wsp/views/wspList.js");
		return <wsp-list/> as WspList;
	}
}

/** Chargement et ouverture d'un wsp par son code ou son alias. */
export class OpenFixedWsp extends Action<IRegPointer<IChainEnv>> {
	constructor(protected _wspCodeOrAlias: string, public wspAppMode?: 'wspApp' | 'wspDocApp', id?: string) {
		super(id || "OpenFixedWsp");
	}

	async execute(ctx: IRegPointer<IChainEnv>, ev?: KeyboardEvent | MouseEvent) {
		const wsp = new Wsp(ctx.reg.env.universe.wspServer, this._wspCodeOrAlias);
		await wsp.waitForLoad();
		if (wsp.isLoaded) {
			const OpenWsp = (await import("/@back@/wsp/views/wspList.js")).OpenWsp;
			return new OpenWsp(this.wspAppMode).execute({reg: REG.createSubReg(ctx.reg, wsp)}, ev);
		} else {
			POPUP.showNotifForbidden("L'atelier demandé n'existe pas ou est inaccessible.", document.body);
		}
	}
}


/** Définition pour une WspApp. */
export interface JWspAppDef extends JAppDef {
	/** Code de l'univers associé */
	u?: string
	wsp: wspCd
	srcRef?: srcRef
}

/** Définition pour une WspDocApp. */
export interface JWspDocAppDef extends JAppDef {
	wspDoc: wspCd
	/** Code de l'univers associé */
	u?: string
	/** document / contenu racine */
	doc?: srcRef
	/** item support sélectionné */
	sup?: srcRef
	/** item courant dans l'arbre affiché */
	src?: srcRef
}


/** Construction async de la view WspApp. */
export class WspAppBuilder extends SimpleTagArea<IAppCtx<IChainEnv>, IApp<IWspEnv>> implements OSkinableInit, IAppBuilder<IChainEnv, IWspEnv> {

	skin?: string;

	skinOver?: string;

	constructor(public reg: IReg<IChainEnv>, public checkUnivers: boolean) {
		super('wsp-app', reg.env.resolver.resolve(":back:wsp/apps/wspApp.js"));
	}

	isAppDefMatch(data: JWspAppDef) {
		return typeof data.wsp === 'string' && (!this.checkUnivers || data.u === this.reg.env.universe.getId());
	}

	normalizeAppDef(data: JWspDocAppDef) {
		if (!this.checkUnivers) data.u = undefined;
	}

	newElt(ctx: IAppCtx<IChainEnv> & OSkinableInit): WspApp {
		if (!ctx.skin) ctx.skin = this.skin;
		if (!ctx.skinOver) ctx.skinOver = this.skinOver;
		ctx.reg = this.reg;
		return (super.newElt(ctx) as WspApp).initialize(ctx as OWspAppInit);
	}
}

/** Construction async de la view WspDocApp. */
export class WspDocAppBuilder extends SimpleTagArea<IAppCtx<IChainEnv>, IApp<IWspEnv>> implements OSkinableInit, IAppBuilder<IChainEnv, IWspEnv> {

	skin?: string;

	skinOver?: string;

	constructor(public reg: IReg<IChainEnv>, public checkUnivers: boolean) {
		super('wsp-doc-app', reg.env.resolver.resolve(":back:wsp/apps/wspDocApp.js"));
	}

	isAppDefMatch(data: JWspDocAppDef) {
		return typeof data.wspDoc === 'string' && (!this.checkUnivers || data.u === this.reg.env.universe.getId());
	}

	normalizeAppDef(data: JWspDocAppDef) {
		if (!this.checkUnivers) data.u = undefined;
	}

	newElt(ctx: IAppCtx<IChainEnv> & OSkinableInit): WspDocApp {
		if (!ctx.skin) ctx.skin = this.skin;
		if (!ctx.skinOver) ctx.skinOver = this.skinOver;
		ctx.reg = this.reg;
		return (super.newElt(ctx) as WspDocApp).initialize(ctx as OWspDocAppInit);
	}
}
